import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ban, Pencil, Plus, Search } from "lucide-react";

import { Badge, Button, useConfirm } from "@/ui";
import { formatEventDate, formatPrice } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import {
  useCancelTraining,
  useTrainings,
  type TrainingLevel,
} from "@/features/queries";

const LEVEL_LABEL: Record<TrainingLevel, string> = {
  all: "Для всех",
  beginner: "Начинающий",
  intermediate: "Средний",
  advanced: "Продвинутый",
};

const LEVEL_KIND: Record<TrainingLevel, "neutral" | "brand" | "warning"> = {
  all: "neutral",
  beginner: "neutral",
  intermediate: "brand",
  advanced: "warning",
};

export function AdminTrainingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: trainings = [], isLoading } = useTrainings();
  const cancel = useCancelTraining();
  const confirm = useConfirm();
  const [search, setSearch] = useState("");

  // Trainers see only their own; admins see everything.
  const visible = user?.is_admin
    ? trainings
    : trainings.filter((t) => t.created_by === user?.id);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return visible;
    return visible.filter((t) => {
      const creator = (t.creator_username ?? t.creator_name ?? "").toLowerCase();
      return (
        t.title.toLowerCase().includes(q) ||
        (t.spot_name ?? "").toLowerCase().includes(q) ||
        creator.includes(q)
      );
    });
  }, [search, visible]);

  return (
    <div>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <h1 className="h1">Тренировки</h1>
        <Button leading={<Plus size={16} />} onClick={() => navigate("/admin/trainings/new")}>
          Создать
        </Button>
      </header>

      {visible.length > 0 && (
        <SearchBar value={search} onChange={setSearch} />
      )}

      {isLoading ? (
        <p className="body">Загрузка…</p>
      ) : visible.length === 0 ? (
        <div
          style={{
            padding: "64px 16px",
            textAlign: "center",
            background: "var(--bg-section)",
            borderRadius: "var(--r-lg)",
          }}
        >
          <h2 className="h2">Тренировок пока нет</h2>
          <p className="body" style={{ color: "var(--fg2)", marginTop: 8 }}>
            Создай первую — она появится в ленте.
          </p>
          <div style={{ marginTop: 16 }}>
            <Button onClick={() => navigate("/admin/trainings/new")}>Создать тренировку</Button>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div
          style={{
            padding: "32px 16px",
            textAlign: "center",
            background: "var(--bg-section)",
            borderRadius: "var(--r-lg)",
            color: "var(--fg2)",
          }}
        >
          Ничего не нашли.
        </div>
      ) : (
        <div
          className="sp-admin-table-wrap"
          style={{ background: "var(--bg-section)", borderRadius: "var(--r-lg)" }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--font-body)" }}>
            <thead className="sp-admin-table-head">
              <tr style={{ background: "var(--bg-secondary)" }}>
                <Th>Название</Th>
                <Th>Когда</Th>
                <Th>Место</Th>
                <Th>Тренер</Th>
                <Th>Уровень</Th>
                <Th>Запись</Th>
                <Th>Цена</Th>
                <Th>Статус</Th>
                <Th>Действия</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const isActive = t.status === "active";
                return (
                  <tr
                    key={t.id}
                    style={{ borderTop: "1px solid var(--separator)" }}
                  >
                    <Td><strong>{t.title}</strong></Td>
                    <Td>{formatEventDate(t.date_time)} · {t.duration} мин</Td>
                    <Td>{t.spot_name ?? "—"}</Td>
                    <Td>{t.creator_username ? `@${t.creator_username}` : t.creator_name ?? "—"}</Td>
                    <Td>
                      <Badge kind={LEVEL_KIND[t.level]}>
                        {LEVEL_LABEL[t.level]}
                      </Badge>
                    </Td>
                    <Td>{t.registered_count ?? 0}/{t.max_participants}</Td>
                    <Td>{formatPrice(t.price)}</Td>
                    <Td>
                      <Badge kind={isActive ? "success" : "neutral"}>
                        {isActive ? "активна" : t.status === "cancelled" ? "отменена" : t.status}
                      </Badge>
                    </Td>
                    <Td>
                      <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
                        <IconAction
                          title="Редактировать"
                          icon={<Pencil size={14} />}
                          disabled={!isActive}
                          onClick={() => navigate(`/admin/trainings/${t.id}/edit`)}
                        />
                        <IconAction
                          title="Отменить"
                          icon={<Ban size={14} />}
                          danger
                          disabled={!isActive || cancel.isPending}
                          onClick={async () => {
                            const ok = await confirm({
                              title: "Отменить тренировку?",
                              description: `«${t.title}» получит статус «отменена», запись будет снята.`,
                              confirmLabel: "Отменить",
                              cancelLabel: "Назад",
                              tone: "danger",
                            });
                            if (ok) cancel.mutate(t.id);
                          }}
                        />
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SearchBar({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: "var(--bg-section)",
        borderRadius: "var(--r-md)",
        padding: "8px 12px",
        marginBottom: 12,
      }}
    >
      <Search size={16} color="var(--fg3)" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Поиск: название, место или @тренер"
        style={{
          flex: 1,
          border: 0,
          outline: 0,
          background: "transparent",
          fontFamily: "var(--font-body)",
          fontSize: 14,
          color: "var(--fg1)",
          minWidth: 0,
        }}
      />
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: "10px 12px",
        fontSize: 12,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: "var(--fg3)",
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td style={{ padding: "12px", fontSize: 14, verticalAlign: "middle" }}>{children}</td>;
}

function IconAction({
  icon,
  title,
  onClick,
  danger,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 32,
        height: 32,
        borderRadius: "var(--r-md)",
        border: 0,
        background: "var(--bg-secondary)",
        color: danger ? "var(--fg-danger)" : "var(--fg1)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {icon}
    </button>
  );
}
